/**
 * Token custody for the auth micro-service.
 *
 * Both tokens are persisted. Keeping the access token in memory only would cost
 * a refresh round trip on every single page load, and the service *rotates* the
 * refresh token on each call — so every reload would burn a rotation, and two
 * tabs booting at once would race for it, the loser being signed out. Reusing a
 * still-valid access token avoids all of that.
 *
 * The security this gives up is small: the refresh token, a 7-day credential
 * that can mint access tokens at will, is already in the same store. An XSS
 * payload that can read one can read the other, and the 15-minute access token
 * is the weaker prize. Rotation is what actually limits the damage — a stolen
 * refresh token, once used, breaks the real user's next refresh and surfaces the
 * compromise.
 *
 * The access token is an RS256 JWT that lives ~15 minutes. The refresh token is
 * an opaque 64-hex-char string valid for 7 days; the app re-mints an access
 * token from it whenever the stored one has expired.
 *
 * The user snapshot from the login response is persisted next to it. The auth
 * service exposes no profile endpoint, so without this snapshot a reload could
 * only recover `sub`/`username`/`roles` from the JWT — losing the display name,
 * the email, and `mustChangePassword`, which is a security-relevant flag.
 * Nothing in the snapshot is a credential; it is the same data the JWT already
 * carries plus two display fields.
 *
 * "Keep me signed in" picks *which* store holds both: `localStorage` survives
 * closing the browser, `sessionStorage` dies with the tab. Either way the token
 * never outlives the 7-day server-side expiry.
 */

const ACCESS_TOKEN_KEY = 'nexqr.merchant.access-token'
const REFRESH_TOKEN_KEY = 'nexqr.merchant.refresh-token'
const USER_KEY = 'nexqr.merchant.user'

type SessionExpiredListener = () => void

let accessToken: string | null = null
const sessionExpiredListeners = new Set<SessionExpiredListener>()

function safeStorage(kind: 'local' | 'session'): Storage | null {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage
  } catch {
    return null
  }
}

function read(key: string): string | null {
  return safeStorage('local')?.getItem(key) ?? safeStorage('session')?.getItem(key) ?? null
}

function write(key: string, value: string | null, persistent: boolean): void {
  // Always clear both first, so toggling the choice cannot strand a copy.
  safeStorage('local')?.removeItem(key)
  safeStorage('session')?.removeItem(key)

  if (!value) return
  safeStorage(persistent ? 'local' : 'session')?.setItem(key, value)
}

/**
 * Which store the session currently occupies — i.e. what the user chose at
 * "keep me signed in". A rotated token must land back in the same store; a
 * blanket `persistent = true` would silently promote a tab-only session to one
 * that survives a browser restart.
 */
function isPersistent(): boolean {
  if (safeStorage('local')?.getItem(REFRESH_TOKEN_KEY)) return true
  if (safeStorage('session')?.getItem(REFRESH_TOKEN_KEY)) return false
  return true
}

export const tokenStorage = {
  getAccessToken(): string | null {
    // The module-level copy is just a cache; storage is the source of truth so
    // a second tab sees a token this one renewed.
    accessToken ??= read(ACCESS_TOKEN_KEY)
    return accessToken
  },

  setAccessToken(token: string | null, persistent = isPersistent()): void {
    accessToken = token
    write(ACCESS_TOKEN_KEY, token, persistent)
  },

  getRefreshToken(): string | null {
    return read(REFRESH_TOKEN_KEY)
  },

  /**
   * @param persistent `true` keeps the session across browser restarts,
   *   `false` confines it to the current tab. Omit it to keep the session
   *   wherever it already lives — which is what a rotation wants.
   */
  setRefreshToken(token: string | null, persistent = isPersistent()): void {
    write(REFRESH_TOKEN_KEY, token, persistent)
  },

  /** The user as the login response described them, or `null` if none is stored. */
  getUser<T>(): T | null {
    const raw = read(USER_KEY)
    if (!raw) return null

    try {
      return JSON.parse(raw) as T
    } catch {
      return null
    }
  },

  setUser(user: unknown, persistent = isPersistent()): void {
    write(USER_KEY, user == null ? null : JSON.stringify(user), persistent)
  },

  clear(): void {
    accessToken = null
    write(ACCESS_TOKEN_KEY, null, true)
    tokenStorage.setRefreshToken(null, true)
    tokenStorage.setUser(null, true)
  },

  /**
   * Called by the HTTP layer when the refresh token is rejected. The auth store
   * subscribes to this so the interceptor never has to import the store — which
   * would create an import cycle.
   */
  onSessionExpired(listener: SessionExpiredListener): () => void {
    sessionExpiredListeners.add(listener)
    return () => sessionExpiredListeners.delete(listener)
  },

  emitSessionExpired(): void {
    for (const listener of sessionExpiredListeners) listener()
  },
}
